import { ReactNode, TableHTMLAttributes, ThHTMLAttributes } from "react";

export enum Direction {
    ASC = "asc",
    DESC = "desc",
    DEFAULT = "default",
}

export interface SortingState {
    columnIndex: number;
    direction: Direction;
}
export interface IPage {
    pageNumber: number;
    pageSize: number;
    offset: number;
}

export interface IPagination {
    pageNumber: number;
    totalRows: number;
    pageSize: number;
    showSizeChanger?: boolean;
}

export interface IEmptyView {
    icon?: ReactNode;
    text?: ReactNode;
}

export type DataValue = string | boolean | number;

export type DefaultCellRenderer<T> = (rowData: T, index?: number) => ReactNode;

export type CellRendererWithValue = (
    value: DataValue,
    index?: number
) => ReactNode;

export type TextAlign = "left" | "center" | "right";

export enum RowSelectionType {
    MULTI = "multi",
}

export interface ITableColumn<T> {
    title: ReactNode;
    id: string;
    dataKey?: string;
    renderer?: DefaultCellRenderer<T>;
    textRenderer?: CellRendererWithValue;
    attributes?: ThHTMLAttributes<HTMLTableCellElement>;
    ellipsis?: boolean;
    align?: TextAlign;
    /**
     * column specific className
     */
    cellClassName?: string;
    /**
     * enable/disable sorting for a column
     */
    sortable?: boolean;
}

interface INativeTableProps extends TableHTMLAttributes<HTMLTableElement> {
    "data-testid"?: string;
}
export interface ITableProps<T> extends INativeTableProps {
    columns: ITableColumn<T>[];
    data: Array<T>;
    rowUniqueKey: string;
    rowHeight?: number;
    rowClassName?: string;
    /**
     * className to apply on all the columns
     */
    columnClassName?: string;
    wrapperClassName?: string;
    pagination?: IPagination;
    hideColumns?: string[];
    isLoading?: boolean;
    loader?: ReactNode;
    scrollable?: boolean;
    onPageChange?: (page: IPage, sorting?: SortingState) => void;
    onRowClick?: (rowData: T, index: number) => void;
    emptyView?: ReactNode | IEmptyView;
    paginationClassName?: string;
    height?: number | "auto";
    stickyHeader?: boolean;
    onSort?: (direction: Direction, columnId: string, data: T[]) => void;
    selectionMode?: `${RowSelectionType}`;
    onSelectionChange?: (
        selectedRows: string[],
        selectedStates?: RowsSelection
    ) => void;
    getCheckBoxState?: (row: T) => {
        isSelected?: boolean;
        isDisabled?: boolean;
    };
}

export interface IColumnMeta<T> extends ITableColumn<T> {
    align?: TextAlign;
    cellClassName?: string;
    attributes?: ThHTMLAttributes<HTMLTableCellElement>;
    ellipsis?: boolean;
    className?: string;
    isActionColumn?: boolean;
    sortable?: boolean;
}

export type RowsState = Record<string | number, boolean>;

export type RowsSelection = RowsState;
